// ── monetization.js — plans, features, limits ────────────────────────────
// Plans: free / honey / royal
// Feature matrix is DB-backed (plan_features table) — loaded via usePlanFeatures()
// Fallback hardcoded matrix used when DB not available.

import { getEffectiveTranslations } from './i18n';

function t(lang, key) {
  return getEffectiveTranslations(lang || 'en')[key] || key;
}

// ── Fallback feature matrix (mirrors DB seed in migration 007) ────────────
// Keyed by plan → feature_key → { enabled, limit_value }
// limit_value: null = unlimited, 0 = disabled, N = hard limit
export const DEFAULT_FEATURE_MATRIX = {
  free: {
    likes_per_day:          { enabled: true,  limit: 10 },
    super_likes_per_day:    { enabled: true,  limit: 1 },
    passes_per_day:         { enabled: true,  limit: null },
    boosts_per_month:       { enabled: false, limit: 0 },
    see_who_liked:          { enabled: false, limit: 0 },
    rewind_last_pass:       { enabled: false, limit: 0 },
    advanced_filters:       { enabled: false, limit: 0 },
    incognito_mode:         { enabled: false, limit: 0 },
    read_receipts:          { enabled: false, limit: 0 },
    profile_prompts:        { enabled: true,  limit: 1 },
    friend_invites:         { enabled: true,  limit: 3 },
    hive_full_access:       { enabled: false, limit: 0 },
    events_access:          { enabled: true,  limit: null },
    scout_access:         { enabled: false, limit: 0 },
    priority_matching:      { enabled: false, limit: 0 },
    vip_events:             { enabled: false, limit: 0 },
    profile_analytics:      { enabled: false, limit: 0 },
    messages_per_convo:     { enabled: true,  limit: 5 },
  },
  honey: {
    likes_per_day:          { enabled: true,  limit: null },
    super_likes_per_day:    { enabled: true,  limit: 5 },
    passes_per_day:         { enabled: true,  limit: null },
    boosts_per_month:       { enabled: true,  limit: 4 },
    see_who_liked:          { enabled: true,  limit: null },
    rewind_last_pass:       { enabled: true,  limit: null },
    advanced_filters:       { enabled: true,  limit: null },
    incognito_mode:         { enabled: true,  limit: null },
    read_receipts:          { enabled: true,  limit: null },
    profile_prompts:        { enabled: true,  limit: 3 },
    friend_invites:         { enabled: true,  limit: null },
    hive_full_access:       { enabled: true,  limit: null },
    events_access:          { enabled: true,  limit: null },
    scout_access:         { enabled: true,  limit: null },
    priority_matching:      { enabled: false, limit: 0 },
    vip_events:             { enabled: false, limit: 0 },
    profile_analytics:      { enabled: false, limit: 0 },
    messages_per_convo:     { enabled: true,  limit: null },
  },
  royal: {
    likes_per_day:          { enabled: true,  limit: null },
    super_likes_per_day:    { enabled: true,  limit: null },
    passes_per_day:         { enabled: true,  limit: null },
    boosts_per_month:       { enabled: true,  limit: 30 },
    see_who_liked:          { enabled: true,  limit: null },
    rewind_last_pass:       { enabled: true,  limit: null },
    advanced_filters:       { enabled: true,  limit: null },
    incognito_mode:         { enabled: true,  limit: null },
    read_receipts:          { enabled: true,  limit: null },
    profile_prompts:        { enabled: true,  limit: null },
    friend_invites:         { enabled: true,  limit: null },
    hive_full_access:       { enabled: true,  limit: null },
    events_access:          { enabled: true,  limit: null },
    scout_access:         { enabled: true,  limit: null },
    priority_matching:      { enabled: true,  limit: null },
    vip_events:             { enabled: true,  limit: null },
    profile_analytics:      { enabled: true,  limit: null },
    messages_per_convo:     { enabled: true,  limit: null },
  },
  // ── Founder — same as Royal, free, plus beta access ──────────────────────
  // Granted via admin invitation. Hidden from the public upgrade page.
  founder: {
    likes_per_day:          { enabled: true,  limit: null },
    super_likes_per_day:    { enabled: true,  limit: null },
    passes_per_day:         { enabled: true,  limit: null },
    boosts_per_month:       { enabled: true,  limit: 30 },
    see_who_liked:          { enabled: true,  limit: null },
    rewind_last_pass:       { enabled: true,  limit: null },
    advanced_filters:       { enabled: true,  limit: null },
    incognito_mode:         { enabled: true,  limit: null },
    read_receipts:          { enabled: true,  limit: null },
    profile_prompts:        { enabled: true,  limit: null },
    friend_invites:         { enabled: true,  limit: null },
    hive_full_access:       { enabled: true,  limit: null },
    events_access:          { enabled: true,  limit: null },
    scout_access:         { enabled: true,  limit: null },
    priority_matching:      { enabled: true,  limit: null },
    vip_events:             { enabled: true,  limit: null },
    profile_analytics:      { enabled: true,  limit: null },
    messages_per_convo:     { enabled: true,  limit: null },
    beta_features:          { enabled: true,  limit: null },
  },
};

// ── Human-readable feature labels ─────────────────────────────────────────
export const FEATURE_LABELS = {
  likes_per_day:       'Likes per day',
  super_likes_per_day: 'Super likes per day',
  passes_per_day:      'Passes per day',
  boosts_per_month:    'Profile boosts / month',
  see_who_liked:       'See who liked you',
  rewind_last_pass:    'Rewind last pass',
  advanced_filters:    'Advanced filters',
  incognito_mode:      'Incognito mode',
  read_receipts:       'Read receipts',
  profile_prompts:     'Profile prompts',
  friend_invites:      'Friend invites',
  hive_full_access:    'Full BeeHive access',
  events_access:       'Events access',
  scout_access:      'Scout',
  priority_matching:   'Priority in Discover',
  vip_events:          'VIP events access',
  profile_analytics:   'Profile analytics',
  messages_per_convo:  'Messages per conversation',
};

// ── Plan definitions ──────────────────────────────────────────────────────
export function getPlans(lang) {
  return {
    free: {
      id: 'free', name: 'BeeLove Free',
      price: 0, period: null, color: '#888',
      badge: null,
      tagline: t(lang, 'plan.free_tag') || 'Start your journey',
      features: [
        '10 likes per day',
        '1 super like per day',
        'Basic discovery',
        'Events access',
        '3 friend invites',
      ],
    },
    honey: {
      id: 'honey', name: 'Bee Honey',
      price: 9.99, period: 'month', color: '#C9A84C',
      badge: '⬡',
      tagline: t(lang, 'plan.honey_tag') || 'Find your match',
      trialDays: 7,
      stripeMonthly: process.env.REACT_APP_STRIPE_HONEY_MONTHLY,
      stripeYearly:  process.env.REACT_APP_STRIPE_HONEY_YEARLY,
      features: [
        'Unlimited likes',
        '5 super likes per day',
        'See who liked you',
        'Advanced filters',
        'Read receipts',
        'Scout access',
        '4 boosts per month',
        'Incognito mode',
      ],
    },
    royal: {
      id: 'royal', name: 'Bee Royal',
      price: 19.99, period: 'month', color: '#E2C06E',
      badge: '★',
      tagline: t(lang, 'plan.royal_tag') || 'The complete experience',
      trialDays: 7,
      stripeMonthly: process.env.REACT_APP_STRIPE_ROYAL_MONTHLY,
      stripeYearly:  process.env.REACT_APP_STRIPE_ROYAL_YEARLY,
      features: [
        'Everything in Honey',
        'Unlimited super likes',
        '30 boosts per month',
        'Priority in Discover',
        'VIP events access',
        'Profile analytics',
        'Unlimited prompts',
        '1-on-1 concierge',
      ],
    },
    // Hidden from public upgrade page — granted by admin invitation only
    founder: {
      id: 'founder', name: 'Bee Founder',
      price: 0, period: null, color: '#4EC994',
      badge: '✦',
      hidden: true,
      tagline: t(lang, 'plan.founder_tag') || 'Founder access',
      features: [
        'All Royal features — free',
        'Early access to new features',
        'Direct feedback channel',
        'Founder badge',
      ],
    },
  };
}

// Backward compat
export const PLANS = getPlans('en');

// ── Check if a feature is enabled for a plan ──────────────────────────────
// featureMatrix: loaded from DB or falls back to DEFAULT_FEATURE_MATRIX
export function canAccess(user, featureKey, featureMatrix = null, subsEnabled = true) {
  // When subscriptions are disabled, all features are accessible
  if (!subsEnabled) return true;
  // founders_only features: only accessible to founding members
  // (handled by feature flag value '__founders_only__' in featureMap)
  const plan   = user?.plan || 'free';
  const matrix = featureMatrix || DEFAULT_FEATURE_MATRIX;
  const entry  = matrix[plan]?.[featureKey];
  if (!entry) return false;
  return entry.enabled && entry.limit !== 0;
}

// ── Get the limit for a feature ───────────────────────────────────────────
export function getLimit(user, featureKey, featureMatrix = null) {
  const plan   = user?.plan || 'free';
  const matrix = featureMatrix || DEFAULT_FEATURE_MATRIX;
  const entry  = matrix[plan]?.[featureKey];
  if (!entry || !entry.enabled) return 0;
  return entry.limit; // null = unlimited
}

// ── Server-side usage check via Supabase RPC ──────────────────────────────
// Returns { allowed, reason, limit, current, remaining, unlimited }
export async function checkUsage(userId, plan, action, increment = true, subsEnabled = true) {
  // When subscriptions are disabled, all actions are unlimited
  if (!subsEnabled) return { allowed: true, unlimited: true, subsDisabled: true };
  try {
    const { supabase } = await import('./supabase');
    const { data } = await supabase?.rpc('check_and_increment_usage', {
      p_user_id:   userId,
      p_plan:      plan || 'free',
      p_action:    action,
      p_increment: increment,
    });
    return data || { allowed: false, reason: 'rpc_failed' };
  } catch {
    return { allowed: true, unlimited: true, fallback: true };
  }
}

// ── Plan from user object ─────────────────────────────────────────────────
export function getPlan(user) {
  return getPlans('en')[user?.plan || 'free'] || getPlans('en').free;
}

// ── À-la-carte items ──────────────────────────────────────────────────────
export function getAlaCarteItems(lang) {
  return [
    { id:'boost_1',      name:'1 Boost',             price:2.99,  icon:'↑' },
    { id:'boost_5',      name:'5 Boosts',             price:9.99,  icon:'↑', badge:'Best value' },
    { id:'superlikes_10',name:'10 Super likes',       price:3.99,  icon:'★' },
    { id:'superlikes_sub',name:'Super likes / month', price:6.99,  icon:'★', period:'month' },
    { id:'stats',        name:'Profile analytics',    price:3.99,  icon:'◈', period:'month' },
    { id:'verified_pro', name:'Verified badge',       price:4.99,  icon:'✦', once:true },
  ];
}
export const ALA_CARTE = getAlaCarteItems('en');

// ── Sponsor / Partner tiers ───────────────────────────────────────────────
export const SPONSOR_TIERS = [
  { id:'bronze', name:'Bronze', price:299,  perks:['Logo in app footer','10 event passes/year'] },
  { id:'silver', name:'Silver', price:799,  perks:['Featured in BeeHive','25 event passes/year','Newsletter'] },
  { id:'gold',   name:'Gold',   price:1999, perks:['Dedicated screen','Unlimited passes','Co-branded content'] },
];
